import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import type { ReactNode } from "react";
import Tag, { type TagVariant } from "~/common/components/Tag/Tag";
import { Card, Collapse, IconButton, Stack, Typography, styled, type CardProps } from "~/common/mui";
import type { StepStatus } from "../../contract/step";

type CollapsedStepAppearance = "completed" | "incomplete" | "error" | "disabled" | "locked";

export interface ConfiguratorStepProps extends Omit<CardProps, "title"> {
  title: string;
  status: StepStatus;
  expanded: boolean;
  error?: boolean;
  completedLabel?: ReactNode;
  incompleteMessage?: ReactNode;
  incompleteLabel?: ReactNode;
  errorMessage?: ReactNode;
  errorLabel?: ReactNode;
  disabledMessage?: ReactNode;
  expandLabel: string;
  collapseLabel: string;
  onToggle?: () => void;
  children?: ReactNode;
}

const getCollapsedAppearance = (status: StepStatus, error: boolean): CollapsedStepAppearance => {
  if (status === "disabled") {
    return "disabled";
  }
  if (status === "locked") {
    return "locked";
  }
  if (error) {
    return "error";
  }
  return status === "completed" ? "completed" : "incomplete";
};

const StepRoot = styled(Card, {
  shouldForwardProp: (prop) => prop !== "expanded" && prop !== "appearance",
})<{ expanded: boolean; appearance: CollapsedStepAppearance }>(({ expanded, appearance, theme }) => {
  const collapsedColors = {
    completed: {
      backgroundColor: theme.palette.surfaceContainer.main,
      color: theme.palette.text.primary,
      opacity: 1,
    },
    incomplete: {
      backgroundColor: theme.palette.surfaceContainer.main,
      color: theme.palette.text.primary,
      opacity: 1,
    },
    error: {
      backgroundColor: theme.palette.surfaceContainer.main,
      color: theme.palette.text.primary,
      opacity: 1,
    },
    disabled: {
      backgroundColor: theme.palette.surfaceContainer.main,
      color: theme.palette.text.primary,
      opacity: theme.palette.action.disabledOpacity,
    },
    locked: {
      backgroundColor: theme.palette.surfaceContainer.low,
      color: theme.palette.text.label,
      opacity: 1,
    },
  }[appearance];

  return {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    border: 0,
    boxShadow: "none",
    backgroundColor: expanded ? theme.palette.surfaceContainer.main : collapsedColors.backgroundColor,
    color: expanded ? theme.palette.text.primary : collapsedColors.color,
    opacity: expanded ? 1 : collapsedColors.opacity,
  };
});

const Header = styled(Stack)({
  width: "100%",
  minWidth: 0,
  alignItems: "center",
});

const TitleRow = styled(Stack)({
  minWidth: 0,
  alignItems: "center",
});

const ToggleButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  width: theme.spacing(3),
  height: theme.spacing(3),
  padding: theme.spacing(0.25),
  color: theme.palette.text.secondary,
}));

const StateTag = styled(Tag)(({ theme }) => ({
  flexShrink: 0,
  gap: theme.spacing(0.5),
}));

const stateTagDefinition = (appearance: CollapsedStepAppearance): { variant: TagVariant; icon: ReactNode } | null => {
  switch (appearance) {
    case "completed":
      return {
        variant: "success",
        icon: <CheckRoundedIcon sx={(theme) => ({ fontSize: theme.spacing(2.5) })} />,
      };
    case "incomplete":
      return {
        variant: "warn",
        icon: <WarningAmberRoundedIcon sx={(theme) => ({ fontSize: theme.spacing(2.5) })} />,
      };
    case "error":
      return {
        variant: "error",
        icon: <ErrorOutlineRoundedIcon sx={(theme) => ({ fontSize: theme.spacing(2.5) })} />,
      };
    case "disabled":
    case "locked":
      return null;
  }
};

const StateLabel = ({ appearance, children }: { appearance: CollapsedStepAppearance; children: ReactNode }) => {
  const definition = stateTagDefinition(appearance);
  return definition ? (
    <StateTag variant={definition.variant} icon={definition.icon} iconPosition="start">
      {children}
    </StateTag>
  ) : null;
};

const getCollapsedContent = (
  appearance: CollapsedStepAppearance,
  incompleteMessage: ReactNode,
  errorMessage: ReactNode,
  disabledMessage: ReactNode,
) => {
  switch (appearance) {
    case "incomplete":
      return incompleteMessage;
    case "error":
      return errorMessage;
    case "disabled":
      return disabledMessage;
    case "locked":
      return null;
  }
};

export const ConfiguratorStep = ({
  title,
  status,
  expanded,
  error = false,
  completedLabel = "Completed",
  incompleteMessage,
  incompleteLabel = "Incomplete",
  errorMessage,
  errorLabel = "There is an issue",
  disabledMessage,
  expandLabel,
  collapseLabel,
  onToggle,
  children,
  ...cardProps
}: ConfiguratorStepProps) => {
  const appearance = getCollapsedAppearance(status, error);
  const effectiveExpanded = status === "disabled" ? false : expanded;
  const toggleDisabled = status === "disabled" || !onToggle;
  const collapsedContent = getCollapsedContent(appearance, incompleteMessage, errorMessage, disabledMessage);
  const stateLabel = {
    completed: completedLabel,
    incomplete: incompleteLabel,
    error: errorLabel,
    disabled: null,
    locked: null,
  }[appearance];

  return (
    <StepRoot {...cardProps} expanded={effectiveExpanded} appearance={appearance}>
      <Header direction="row" gap={2}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <TitleRow direction="row" gap={1}>
            <Typography variant="titleLarge" sx={{ color: "inherit", fontWeight: 700 }}>
              {title}
            </Typography>
            {(!effectiveExpanded || appearance === "completed") && stateLabel ? (
              <StateLabel appearance={appearance}>{stateLabel}</StateLabel>
            ) : null}
          </TitleRow>
          {!effectiveExpanded && collapsedContent ? (
            <Typography
              variant="bodyMedium"
              sx={(theme) => ({
                color: appearance === "disabled" ? theme.palette.text.primary : theme.palette.text.secondary,
              })}
            >
              {collapsedContent}
            </Typography>
          ) : null}
        </Stack>
        <ToggleButton
          aria-label={effectiveExpanded ? collapseLabel : expandLabel}
          aria-expanded={effectiveExpanded}
          disabled={toggleDisabled}
          onClick={onToggle}
        >
          {effectiveExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
        </ToggleButton>
      </Header>
      <Collapse in={effectiveExpanded}>
        <Stack gap={2} sx={(theme) => ({ paddingTop: theme.spacing(2) })}>
          {children}
        </Stack>
      </Collapse>
    </StepRoot>
  );
};
